import {
  OrgSubjectsService,
  OrganizationSubjectPublicResponse,
  Auth0AccountRole,
  TutorLimitedResponse,
  TutorSubjectPublicResponse,
  TutorSubjectsService,
} from "client/openapi";
import { useState, useEffect, useContext, useCallback } from "react";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { Select } from "components/Select";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";
import { ReaderIcon } from "@radix-ui/react-icons";

export default function Subjects({
  tutor,
  organization_id,
}: {
  tutor: TutorLimitedResponse;
  organization_id: number;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const [tutorSubjects, setTutorSubjects] = useState<
    TutorSubjectPublicResponse[]
  >([]);
  const [orgSubjects, setOrgSubjects] = useState<
    OrganizationSubjectPublicResponse[]
  >([]);
  const [availableSubjects, setAvailableSubjects] = useState<
    OrganizationSubjectPublicResponse[]
  >([]);
  const [addingSubject, setAddingSubject] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [deleteIntentKey, setDeleteIntentKey] = useState<number>();

  // Get all tutor subjects for this tutor
  const getTutorSubjects = useCallback(async () => {
    TutorSubjectsService.getSubjectsByTutor({
      tutorId: tutor.id,
      active: true,
    })
      .then((subjects) => {
        const activeSubjects = subjects.filter((subject) => subject.active);
        setTutorSubjects(activeSubjects);
      })
      .catch((error) => {
        console.error("Error fetching tutor subjects:", error);
        Notifications.error("Unable to load tutor subjects");
      });
  }, [tutor.id]);

  // Get all organization subjects
  const getOrgSubjects = useCallback(async () => {
    OrgSubjectsService.getSubjectsByOrg({
      orgId: organization_id,
      active: true,
    })
      .then((subjects) => {
        setOrgSubjects(subjects as OrganizationSubjectPublicResponse[]);
      })
      .catch((error) => {
        console.error("Error fetching organization subjects:", error);
        Notifications.error("Unable to load organization subjects");
      });
  }, [organization_id]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([getTutorSubjects(), getOrgSubjects()]);
      setLoading(false);
    };
    loadData();
  }, [tutor.id, organization_id, getTutorSubjects, getOrgSubjects]);

  // Update available subjects when org subjects or student subjects change
  useEffect(() => {
    if (orgSubjects.length === 0) {
      setAvailableSubjects([]);
      return;
    }

    const available = orgSubjects.filter(
      (orgSubject) =>
        !tutorSubjects.some(
          (tutorSubject) => tutorSubject.org_subject.id === orgSubject.id
        )
    );
    setAvailableSubjects(available);
  }, [orgSubjects, tutorSubjects]);

  // Handle adding a subject to the student
  const handleAddSubject = async (
    orgSubject: OrganizationSubjectPublicResponse
  ) => {
    TutorSubjectsService.toggleTutorSubjectActive({
      tutorId: tutor.id,
      orgSubjectId: orgSubject.id,
      active: true,
    })
      .then((newTutorSubject) => {
        // Update local state directly
        setTutorSubjects((prev) => [...prev, newTutorSubject]);
        setAvailableSubjects((prev) =>
          prev.filter((subject) => subject.id !== orgSubject.id)
        );
        Notifications.success(
          `${orgSubject.name} successfully added to ${tutor.first_name}`
        );
        setAddingSubject(false);
      })
      .catch((error) => {
        console.error("Error adding subject:", error);
        Notifications.error(
          `Unable to add ${orgSubject.name} to ${tutor.first_name}`
        );
      });
  };

  // Handle removing a tutor subject from the tutor (toggle active to false)
  const handleRemoveSubject = async (
    tutorSubject: TutorSubjectPublicResponse
  ) => {
    TutorSubjectsService.toggleTutorSubjectActive({
      tutorId: tutor.id,
      orgSubjectId: tutorSubject.org_subject.id,
      active: false,
    })
      .then(() => {
        // Update local state directly
        setTutorSubjects((prev) =>
          prev.filter((subject) => subject.id !== tutorSubject.id)
        );
        setAvailableSubjects((prev) => [...prev, tutorSubject.org_subject]);
        Notifications.success(
          `${tutorSubject.org_subject.name} is no longer linked to ${tutor.first_name}`
        );
        setDeleteIntentKey(-1);
      })
      .catch((error) => {
        console.error("Error removing subject:", error);
        Notifications.error(
          `Unable to remove ${tutorSubject.org_subject.name} from ${tutor.first_name}`
        );
      });
  };

  if (loading) {
    return (
      <section className="student-profile--section" id="student-subjects">
        <div className="student-profile--section-header">
          <SectionTitle icon={<ReaderIcon />} tone={BubbleTone.TEAL}>
            Subjects
          </SectionTitle>
        </div>
        <div className="parent-profile--section-items">
          <div className="parent-profile--section-item">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="student-profile--section" id="student-subjects">
      <div className="student-profile--section-header">
        <SectionTitle icon={<ReaderIcon />} tone={BubbleTone.TEAL}>
          Subjects
        </SectionTitle>

        {addingSubject ? (
          <Select
            id="add-subject"
            name="add-subject"
            options={availableSubjects}
            value={undefined}
            isDisabled={availableSubjects?.length === 0}
            getOptionLabel={(s) => s.name}
            getOptionValue={(s) => s.id.toString()}
            onChange={(val) => {
              handleAddSubject(val as OrganizationSubjectPublicResponse);
            }}
            className="w-36 h-9 text-sm"
            placeholder="Select subject..."
          />
        ) : (
          <div className="flex flex-row gap-1.5 items-center flex-shrink-0">
            {[Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
              currently_selected_role as Auth0AccountRole
            ) && (
              <Button
                color={ButtonColor.SKYBLUE}
                size={ButtonSize.SMALL}
                onClick={() => setAddingSubject(true)}
                disabled={availableSubjects.length === 0}
              >
                <span className="hidden sm:inline">Add Subject</span>
                <span className="sm:hidden">Add</span>
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="parent-profile--section-items max-h-[380px] overflow-y-auto">
        {tutorSubjects.length > 0 ? (
          tutorSubjects.map((tutorSubject, i) => (
            <div
              key={i}
              className="parent-profile--section-item parent-detail--student"
            >
              <div className="parent-detail--student-details">
                <p className="font-medium">{tutorSubject.org_subject.name}</p>
                {tutorSubject.org_subject.description && (
                  <p className="text-sm text-gray-600">
                    {tutorSubject.org_subject.description}
                  </p>
                )}
                {[
                  Auth0AccountRole.ORG_ADMIN,
                  Auth0AccountRole.ORG_TUTOR,
                ].includes(currently_selected_role as Auth0AccountRole) && (
                  <div className="mt-2">
                    {deleteIntentKey === tutorSubject.id ? (
                      <>
                        <span style={{ marginRight: "5px" }}>
                          <Button
                            onClick={() => {
                              handleRemoveSubject(tutorSubject);
                            }}
                            color={ButtonColor.RED}
                            size={ButtonSize.EXTRA_SMALL}
                          >
                            confirm removal
                          </Button>
                        </span>
                        <span>
                          <Button
                            onClick={() => setDeleteIntentKey(-1)}
                            color={ButtonColor.SKYBLUE}
                            size={ButtonSize.EXTRA_SMALL}
                          >
                            cancel
                          </Button>
                        </span>
                      </>
                    ) : (
                      <span>
                        <Button
                          color={ButtonColor.SKYBLUE}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.EXTRA_SMALL}
                          onClick={() => setDeleteIntentKey(tutorSubject.id)}
                        >
                          remove
                        </Button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="parent-profile--section-item">
            No subjects assigned.
          </div>
        )}
      </div>
    </section>
  );
}
